import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { hideChildModal, hideModal, showChildModal } from "../../data/modal";
import { Terminal } from "../../types/Terminal";
import { getSelectedTerminalCalls } from "../../data/selectedterminalcalls";
import {
  selectedFilteredTerminalCall,
  selectedFilteredVoyage,
} from "../../selectors/filterSelector";
import { normalizeIds } from "../utils/normalize";
import { runModalCallback } from "../../data/modalCallbacks";
import { textColorHandler } from "../Helpers.tsx";

import { MapContainer, Marker, Popup, TileLayer } from "react-leaflet";

import {
  Alert,
  Box,
  Button,
  Chip,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";

import {
  Close as CloseIcon,
  Edit as EditIcon,
  InsertDriveFileSharp as InsertDriveFileSharpIcon,
  Image as ImageIcon,
  Link as LinkIcon,
  PictureAsPdf as PictureAsPdfIcon,
} from "@mui/icons-material";

interface TerminalMapModalProps {
  terminal?: Terminal;
  callback?: (data?: any) => Promise<void> | void;
  callbackId?: string;
}

const DEFAULT_TERMINAL_COLOR = "#FFE4C4";

const hexToRgba = (hex?: string | null, opacity = 0.12) => {
  if (!hex) {
    return `rgba(255, 228, 196, ${opacity})`;
  }

  const normalizedHex = hex.replace("#", "");

  if (![3, 6].includes(normalizedHex.length)) {
    return `rgba(255, 228, 196, ${opacity})`;
  }

  const fullHex =
    normalizedHex.length === 3
      ? normalizedHex
          .split("")
          .map((char) => char + char)
          .join("")
      : normalizedHex;

  const red = parseInt(fullHex.slice(0, 2), 16);
  const green = parseInt(fullHex.slice(2, 4), 16);
  const blue = parseInt(fullHex.slice(4, 6), 16);

  if ([red, green, blue].some((value) => Number.isNaN(value))) {
    return `rgba(255, 228, 196, ${opacity})`;
  }

  return `rgba(${red}, ${green}, ${blue}, ${opacity})`;
};

const getEntityId = (entity: any) => {
  return entity?.["@id"] ?? entity?.id ?? null;
};

const getFileName = (file: any) => {
  return (
    file?.description ??
    file?.discription ??
    file?.name ??
    file?.fileName ??
    file?.filename ??
    "File"
  );
};

const getFileContentUrl = (file: any) => {
  return file?.contentUrl ?? file?.url ?? "";
};

const getFileExtension = (file: any) => {
  const fileUrl = getFileContentUrl(file);
  const fileName = getFileName(file);
  const source = fileUrl || fileName;

  const cleanSource = source.split("?")[0].split("#")[0];
  const extension = cleanSource.split(".").pop();

  return extension ? extension.toLowerCase() : "";
};

const isImageFile = (file: any) => {
  return ["png", "jpg", "jpeg", "gif", "webp", "svg"].includes(
    getFileExtension(file)
  );
};

const isPdfFile = (file: any) => {
  return getFileExtension(file) === "pdf";
};

const FileThumbnailIcon = ({ file }: any) => {
  if (isImageFile(file)) {
    return <ImageIcon fontSize="large" />;
  }

  if (isPdfFile(file)) {
    return <PictureAsPdfIcon fontSize="large" />;
  }

  return <InsertDriveFileSharpIcon fontSize="large" />;
};

const normalizeLinkUrl = (url?: string | null) => {
  const trimmedUrl = url?.trim();

  if (!trimmedUrl) {
    return "";
  }

  if (/^https?:\/\//i.test(trimmedUrl)) {
    return trimmedUrl;
  }

  return `https://${trimmedUrl}`;
};

const getLinkName = (link: any, index: number) => {
  return link?.name?.trim() || link?.url?.trim() || `Link ${index + 1}`;
};

const TerminalMapModal: React.FC<TerminalMapModalProps> = ({
  terminal,
  callback,
  callbackId,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const { voyage } = useSelector(selectedFilteredVoyage);
  const filteredTerminalCall = useSelector(selectedFilteredTerminalCall);
  const modal = useSelector((state: any) => state.modal);

  const [currentTerminal, setCurrentTerminal] = useState<Terminal | undefined>(
    terminal
  );

  useEffect(() => {
    setCurrentTerminal(terminal);
  }, [terminal]);

  const openChildModal = useCallback(
    (opts: any) => dispatch(showChildModal(opts) as any),
    [dispatch]
  );

  const refreshSelectedTerminalCalls = useCallback(async () => {
    if (!voyage?.terminalCalls) {
      return;
    }

    await dispatch(
      getSelectedTerminalCalls(normalizeIds(voyage.terminalCalls)) as any
    );
  }, [dispatch, voyage]);

  const refreshCallback = useCallback(
    async (savedTerminal?: any) => {
      if (savedTerminal) {
        setCurrentTerminal((previousTerminal: any) => ({
          ...(previousTerminal ?? {}),
          ...savedTerminal,
        }));
      }

      await refreshSelectedTerminalCalls();

      if (callback) {
        await callback(savedTerminal);
      }

      if (callbackId) {
        await runModalCallback(callbackId, savedTerminal);
      }
    },
    [callback, callbackId, refreshSelectedTerminalCalls]
  );

  const activeTerminal = useMemo(() => {
    if (!currentTerminal) {
      return undefined;
    }

    const currentTerminalId = getEntityId(currentTerminal);
    const filteredTerminal = filteredTerminalCall?.terminal;
    const filteredTerminalId = getEntityId(filteredTerminal);

    if (
      filteredTerminal &&
      currentTerminalId &&
      filteredTerminalId &&
      currentTerminalId === filteredTerminalId
    ) {
      return {
        ...currentTerminal,
        ...filteredTerminal,
      };
    }

    return currentTerminal;
  }, [currentTerminal, filteredTerminalCall]);

  const terminalColor = useMemo(() => {
    return activeTerminal?.color || DEFAULT_TERMINAL_COLOR;
  }, [activeTerminal]);

  const terminalTextColor = useMemo(() => {
    return textColorHandler(terminalColor);
  }, [terminalColor]);

  const terminalSoftBackground = useMemo(() => {
    return hexToRgba(terminalColor, 0.12);
  }, [terminalColor]);

  const terminalHoverBackground = useMemo(() => {
    return hexToRgba(terminalColor, 0.18);
  }, [terminalColor]);

  const terminalBorderBackground = useMemo(() => {
    return hexToRgba(terminalColor, 0.28);
  }, [terminalColor]);

  if (!activeTerminal) {
    return null;
  }

  const activeTerminalId = getEntityId(activeTerminal) ?? "terminal";

  const name = `${
    activeTerminal.abbreviation ? `${activeTerminal.abbreviation} - ` : ""
  }${activeTerminal.name}`;

  const hasCoordinates =
    activeTerminal.latitude !== null &&
    activeTerminal.latitude !== undefined &&
    activeTerminal.longitude !== null &&
    activeTerminal.longitude !== undefined;

  const coordinates: [number, number] = hasCoordinates
    ? [Number(activeTerminal.latitude), Number(activeTerminal.longitude)]
    : [51.23102, 4.3979];

  const aliases = Array.isArray(activeTerminal.aliases)
    ? activeTerminal.aliases.filter(
        (alias) => typeof alias === "string" && alias.trim() !== ""
      )
    : [];

  const terminalLinks = Array.isArray((activeTerminal as any)?.links)
    ? (activeTerminal as any).links.filter(
        (link: any) => link?.name?.trim() || link?.url?.trim()
      )
    : [];

  const terminalFiles =
    (activeTerminal as any)?.fileGroup?.files &&
    Array.isArray((activeTerminal as any).fileGroup.files)
      ? (activeTerminal as any).fileGroup.files
      : [];

  const handleCloseForm = () => {
    if (modal.childModal) {
      dispatch(hideChildModal() as any);
      return;
    }

    dispatch(hideModal() as any);
  };

  const openFilePreviewModal = (file: any) => {
    openChildModal({
      title: t("viewFile"),
      description: getFileName(file),
      form: "fileview",
      formProps: {
        file,
        isChildModal: true,
      },
      size: "default",
    });
  };

  const openLink = (url?: string | null) => {
    const normalizedUrl = normalizeLinkUrl(url);

    if (!normalizedUrl) {
      return;
    }

    window.open(normalizedUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <Box>
      <Grid container spacing={2} pb={2} alignItems="stretch">
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            display: "flex",
            minHeight: {
              xs: 400,
              md: "auto",
            },
          }}
        >
          <Paper
            variant="outlined"
            sx={{
              width: "100%",
              minHeight: {
                xs: 400,
                md: "100%",
              },
              height: {
                xs: 400,
                md: "auto",
              },
              borderRadius: 1,
              overflow: "hidden",
              display: "flex",
              borderColor: terminalBorderBackground,
            }}
          >
            <Box
              sx={{
                flex: 1,
                minHeight: {
                  xs: 400,
                  md: "100%",
                },
                "& .leaflet-container": {
                  height: "100%",
                  width: "100%",
                },
              }}
            >
              <MapContainer
                key={`${activeTerminalId}-${coordinates[0]}-${coordinates[1]}`}
                center={coordinates}
                zoom={12}
                scrollWheelZoom={false}
                style={{
                  height: "100%",
                  width: "100%",
                }}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />

                <Marker position={coordinates}>
                  <Popup>{name}</Popup>
                </Marker>
              </MapContainer>
            </Box>
          </Paper>
        </Grid>

        <Grid
          item
          xs={12}
          md={6}
          sx={{
            display: "flex",
          }}
        >
          <Paper
            variant="outlined"
            sx={{
              borderRadius: 1,
              width: "100%",
              backgroundColor: "background.paper",
              overflow: "hidden",
              borderColor: terminalBorderBackground,
            }}
          >
            <Box
              sx={{
                px: 2,
                py: 1.75,
                background: `linear-gradient(90deg, ${terminalSoftBackground} 0%, rgba(255,255,255,0.94) 76%)`,
                borderBottom: "1px solid",
                borderColor: "divider",
              }}
            >
              <Stack direction="row" spacing={1.25} alignItems="center">
                <Box
                  sx={{
                    width: 22,
                    height: 22,
                    minWidth: 22,
                    borderRadius: "50%",
                    backgroundColor: `${terminalColor} !important`,
                    color: terminalTextColor,
                    border: "2px solid rgba(255,255,255,0.9)",
                  }}
                />

                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="overline" color="text.secondary">
                    {t("terminal")}
                  </Typography>

                  <Typography
                    variant="h6"
                    lineHeight={1.2}
                    fontWeight={800}
                    noWrap
                  >
                    {name}
                  </Typography>
                </Box>
              </Stack>

              {activeTerminal.description ? (
                <Typography variant="body2" color="text.secondary" mt={1}>
                  {activeTerminal.description}
                </Typography>
              ) : null}
            </Box>

            <Box sx={{ p: 2 }}>
              <Stack spacing={2}>
                <Box>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Chip
                      size="small"
                      label={`${t("city")}: ${activeTerminal.city ?? "N/A"}`}
                      sx={{
                        backgroundColor: terminalSoftBackground,
                        borderColor: terminalBorderBackground,
                      }}
                    />

                    {activeTerminal.country ? (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`${t("country")}: ${activeTerminal.country}`}
                        sx={{
                          borderColor: terminalBorderBackground,
                        }}
                      />
                    ) : null}

                    {aliases.map((alias, index) => (
                      <Chip
                        key={`terminal-alias-${index}`}
                        size="small"
                        variant="outlined"
                        label={alias}
                        sx={{
                          borderColor: terminalBorderBackground,
                        }}
                      />
                    ))}
                  </Stack>
                </Box>

                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    {t("links")}
                  </Typography>

                  {terminalLinks.length > 0 ? (
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {terminalLinks.map((link: any, index: number) => {
                        const linkName = getLinkName(link, index);
                        const normalizedUrl = normalizeLinkUrl(link?.url);

                        return (
                          <Tooltip
                            key={`terminal-link-${index}`}
                            title={normalizedUrl || linkName}
                          >
                            <Chip
                              clickable={Boolean(normalizedUrl)}
                              icon={<LinkIcon />}
                              label={linkName}
                              onClick={() => openLink(link?.url)}
                              variant="outlined"
                              sx={{
                                borderColor: terminalBorderBackground,
                                backgroundColor: terminalSoftBackground,
                                color: "text.primary",
                                fontWeight: 600,
                                "& .MuiChip-icon": {
                                  color: terminalColor,
                                },
                                "&:hover": {
                                  borderColor: terminalColor,
                                  backgroundColor: terminalHoverBackground,
                                },
                              }}
                            />
                          </Tooltip>
                        );
                      })}
                    </Stack>
                  ) : (
                    <Alert severity="info">{t("noLinksForTerminal")}</Alert>
                  )}
                </Box>

                {/* <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    {t("files")}
                  </Typography>

                  {terminalFiles.length > 0 ? (
                    <Grid container spacing={1}>
                      {terminalFiles.map((file: any, index: number) => {
                        const fileName = getFileName(file);
                        const contentUrl = getFileContentUrl(file);
                        const fileKey =
                          file?.["@id"] ??
                          file?.id ??
                          `terminal-file-${index}`;

                        return (
                          <Grid item xs={4} sm={3} md={4} key={fileKey}>
                            <Tooltip title={fileName}>
                              <Box
                                onClick={() => openFilePreviewModal(file)}
                                sx={{
                                  position: "relative",
                                  height: 84,
                                  borderRadius: 1.5,
                                  border: "1px solid",
                                  borderColor: "divider",
                                  overflow: "hidden",
                                  backgroundColor: "background.default",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  cursor: "pointer",
                                  transition:
                                    "border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease",
                                  "&:hover": {
                                    borderColor: terminalColor,
                                    transform: "translateY(-1px)",
                                  },
                                }}
                              >
                                {isImageFile(file) && contentUrl ? (
                                  <Box
                                    component="img"
                                    src={contentUrl}
                                    alt={fileName}
                                    sx={{
                                      width: "100%",
                                      height: "100%",
                                      objectFit: "cover",
                                    }}
                                  />
                                ) : (
                                  <Box
                                    sx={{
                                      color: "text.secondary",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                    }}
                                  >
                                    <FileThumbnailIcon file={file} />
                                  </Box>
                                )}

                                <Box
                                  sx={{
                                    position: "absolute",
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    px: 0.75,
                                    py: 0.5,
                                    background:
                                      "linear-gradient(to top, rgba(0,0,0,0.72), rgba(0,0,0,0.08))",
                                    color: "common.white",
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    noWrap
                                    sx={{
                                      display: "block",
                                      lineHeight: 1.1,
                                    }}
                                  >
                                    {fileName}
                                  </Typography>
                                </Box>
                              </Box>
                            </Tooltip>
                          </Grid>
                        );
                      })}
                    </Grid>
                  ) : (
                    <Alert severity="info">{t("noFilesForTerminal")}</Alert>
                  )}
                </Box> */}

                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    {t("contacts")}
                  </Typography>

                  {activeTerminal.contacts &&
                  activeTerminal.contacts.length > 0 ? (
                    <Stack spacing={1}>
                      {activeTerminal.contacts.map(
                        (contact: any, index: number) => (
                          <Paper
                            variant="outlined"
                            sx={{
                              p: 1.5,
                              borderRadius: 1.5,
                              backgroundColor: "background.default",
                              borderColor: "divider",
                              transition:
                                "border-color 0.2s ease, background-color 0.2s ease",
                              "&:hover": {
                                borderColor: terminalColor,
                              },
                            }}
                            key={`contact-${index}`}
                          >
                            <Table size="small">
                              <TableBody>
                                <TableRow>
                                  <TableCell
                                    sx={{
                                      width: 90,
                                      borderBottom: "none",
                                      color: "text.secondary",
                                      py: 0.25,
                                    }}
                                  >
                                    {t("name")}
                                  </TableCell>

                                  <TableCell
                                    sx={{
                                      borderBottom: "none",
                                      py: 0.25,
                                    }}
                                  >
                                    {contact.name || "—"}
                                  </TableCell>
                                </TableRow>

                                <TableRow>
                                  <TableCell
                                    sx={{
                                      width: 90,
                                      borderBottom: "none",
                                      color: "text.secondary",
                                      py: 0.25,
                                    }}
                                  >
                                    {t("phone")}
                                  </TableCell>

                                  <TableCell
                                    sx={{
                                      borderBottom: "none",
                                      py: 0.25,
                                    }}
                                  >
                                    {contact.phoneNumber || "—"}
                                  </TableCell>
                                </TableRow>

                                <TableRow>
                                  <TableCell
                                    sx={{
                                      width: 90,
                                      borderBottom: "none",
                                      color: "text.secondary",
                                      py: 0.25,
                                    }}
                                  >
                                    {t("email")}
                                  </TableCell>

                                  <TableCell
                                    sx={{
                                      borderBottom: "none",
                                      py: 0.25,
                                    }}
                                  >
                                    {contact.email || "—"}
                                  </TableCell>
                                </TableRow>
                              </TableBody>
                            </Table>
                          </Paper>
                        )
                      )}
                    </Stack>
                  ) : (
                    <Alert severity="info">{t("noContactsForTerminal")}</Alert>
                  )}
                </Box>
              </Stack>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <Grid container className="button-wrapper" justifyContent="space-between">
        <Button onClick={handleCloseForm} variant="contained" size="small">
          <CloseIcon fontSize="inherit" />
          &nbsp;{t("close")}
        </Button>

        <Button
          type="button"
          variant="contained"
          size="small"
          onClick={() =>
            openChildModal({
              title: t("editTerminal"),
              description: t("modifyTerminalDescription"),
              form: "terminal",
              formProps: {
                terminal: activeTerminal,
                callback: refreshCallback,
                isChildModal: true,
              },
            })
          }
        >
          <EditIcon fontSize="inherit" />
          &nbsp;{t("edit")}
        </Button>
      </Grid>
    </Box>
  );
};

export default TerminalMapModal;